import {
    FeatureFlagIdAccountProvisioning,
    FeatureFlagIdBillingDashboard,
    FeatureFlagIdBranding,
    FeatureFlagIdOauth2Dcr,
    FeatureFlagIdSystemManagement,
} from 'c1gen/features'
import { TenantV1Tenantowner, VaultV1VaultServiceowner } from 'c1gen/roles'
import { SettingsGroup } from 'components/modular-nav/types'
import { routes } from 'lib/routes'
import {
    Bell,
    Bot,
    Building2,
    ChartNoAxesCombined,
    Database,
    KeySquare,
    Lock,
    Paintbrush,
    Scroll,
    SlidersHorizontal,
    Tag,
} from 'lucide-react'
import { FormattedMessage } from 'react-intl'

/**
 * Settings navigation configuration for the new nav (NEW_NAV_2026 feature flag)
 *
 * Settings use a grouped structure displayed in the SettingsSidebarOverlay
 */
export const settingsNavigation: SettingsGroup[] = [
    {
        id: 'tenant',
        label: <FormattedMessage id="nav_settings_group_tenant" defaultMessage="Tenant" />,
        items: [
            {
                id: 'organization',
                label: <FormattedMessage id="nav_settings_organization" defaultMessage="Organization" />,
                icon: Building2,
                pathname: routes.settings.organization,
            },
            {
                id: 'branding',
                label: <FormattedMessage id="nav_settings_branding" defaultMessage="Branding" />,
                icon: Paintbrush,
                pathname: routes.settings.branding,
                features: [FeatureFlagIdBranding],
            },
            {
                id: 'notifications',
                label: <FormattedMessage id="nav_settings_notifications" defaultMessage="Notifications" />,
                icon: Bell,
                pathname: routes.settings.notifications,
            },
            {
                id: 'tags',
                label: <FormattedMessage id="nav_settings_tags" defaultMessage="Tags" />,
                icon: Tag,
                pathname: routes.settings.tags,
            },
            {
                id: 'system-management',
                label: <FormattedMessage id="nav_settings_system_management" defaultMessage="System management" />,
                icon: SlidersHorizontal,
                pathname: routes.settings.systemManagement,
                features: [FeatureFlagIdSystemManagement],
            },
            {
                id: 'billing-and-usage',
                label: <FormattedMessage id="nav_settings_billing_and_usage" defaultMessage="Billing and usage" />,
                icon: ChartNoAxesCombined,
                pathname: routes.settings.billingAndUsage,
                features: [FeatureFlagIdBillingDashboard],
            },
        ],
    },
    {
        id: 'security',
        label: <FormattedMessage id="nav_settings_group_security" defaultMessage="Security" />,
        items: [
            {
                id: 'sso-sessions',
                label: <FormattedMessage id="nav_settings_sso_sessions" defaultMessage="SSO & Sessions" />,
                icon: Lock,
                pathname: routes.settings.sso,
            },
            {
                id: 'vaults',
                label: <FormattedMessage id="nav_settings_vaults" defaultMessage="Vaults" />,
                icon: Database,
                pathname: routes.settings.vaults.list,
                roles: [VaultV1VaultServiceowner],
                features: [FeatureFlagIdAccountProvisioning],
            },
        ],
    },
    {
        id: 'developers',
        label: <FormattedMessage id="nav_settings_group_developers" defaultMessage="Developers" />,
        items: [
            {
                id: 'api-keys',
                label: <FormattedMessage id="nav_settings_api_keys" defaultMessage="API keys" />,
                icon: KeySquare,
                pathname: routes.settings.apiKeys,
            },
            {
                id: 'ai-connections',
                label: <FormattedMessage id="nav_settings_ai_connections" defaultMessage="AI connections" />,
                icon: Bot,
                pathname: routes.settings.aiConnections,
                features: [FeatureFlagIdOauth2Dcr],
                // Restrict to Super Admin (TenantV1Tenantowner) and Read Only Admin (handled by isReadOnlySuperAdmin check)
                roles: [TenantV1Tenantowner],
            },
            {
                id: 'system-log',
                label: <FormattedMessage id="nav_settings_system_log" defaultMessage="System log" />,
                icon: Scroll,
                pathname: routes.settings.systemLog,
            },
        ],
    },
]

export const getAllSettingsPaths = (): string[] => {
    const paths: string[] = []
    settingsNavigation.forEach((group) => {
        group.items.forEach((item) => {
            paths.push(item.pathname)
            if (item.selectedPaths) {
                paths.push(...item.selectedPaths)
            }
        })
    })
    return paths
}
